import { useEffect } from "react";

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://wtddetailing.ca";
const BRAND = "WTD Detailing & Tint";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, obj) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

function removeJsonLd(id) {
  const el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (el) el.remove();
}

/**
 * SEO — declarative meta/canonical/OG/Twitter/JSON-LD injector for SPA routes.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  keywords,
  jsonLd = [],
  noindex = false,
}) {
  const fullTitle = title
    ? `${title} | ${BRAND}`
    : `${BRAND} — Premium Auto Protection in Edmonton`;
  const url = `${SITE_URL}${path}`;
  const ogImage = image || `${SITE_URL}/og-image.jpg`;

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta("name", "description", description || "");
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    // Canonical
    upsertLink("canonical", url);

    // Open Graph
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description || "");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:site_name", BRAND);
    upsertMeta("property", "og:locale", "en_CA");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description || "");
    upsertMeta("name", "twitter:image", ogImage);

    // JSON-LD
    const ids = [];
    (jsonLd || []).forEach((obj, i) => {
      const id = `seo-${i}`;
      ids.push(id);
      upsertJsonLd(id, obj);
    });

    return () => {
      ids.forEach(removeJsonLd);
    };
  }, [fullTitle, description, keywords, url, ogImage, noindex, jsonLd]);

  return null;
}

// Reusable structured data builders
export function localBusinessSchema({ contact }) {
  return {
    "@context": "https://schema.org",
    "@type": ["AutoBodyShop", "AutomotiveBusiness"],
    "@id": `${SITE_URL}/#business`,
    name: BRAND,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    telephone: contact.phone,
    email: contact.email,
    image: `${SITE_URL}/og-image.jpg`,
    address: {
      "@type": "PostalAddress",
      streetAddress: "11629 — 145 Street NW",
      addressLocality: "Edmonton",
      addressRegion: "AB",
      postalCode: "T5M 1V9",
      addressCountry: "CA",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "18:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "10:00",
        closes: "16:00",
      },
    ],
    areaServed: [
      { "@type": "City", name: "Edmonton" },
      { "@type": "City", name: "Sherwood Park" },
      { "@type": "City", name: "St. Albert" },
      { "@type": "City", name: "Leduc" },
      { "@type": "City", name: "Spruce Grove" },
    ],
  };
}

export function serviceSchema({ name, description, slug, price }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    provider: { "@id": `${SITE_URL}/#business` },
    areaServed: { "@type": "City", name: "Edmonton" },
    url: `${SITE_URL}/${slug}`,
    ...(price ? { offers: { "@type": "Offer", priceCurrency: "CAD", price } } : {}),
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function reviewsAggregateSchema(reviews) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${BRAND} Services`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: String(reviews.length),
    },
    review: reviews.map((r) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(r.rating),
        bestRating: "5",
      },
      author: { "@type": "Person", name: r.name },
      reviewBody: r.body,
    })),
  };
}
